import type { ModelInfo, User } from './index.ts';
import type { ModelPricing } from './usage.ts';

/** Identifier syntax only. The server registry, never this predicate, grants access. */
export function isModelId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,199}$/.test(value);
}
export const MODEL_REASONING_EFFORTS = ['none','minimal','low','medium','high','xhigh','max','ultra'] as const;
export type ModelReasoningEffort = typeof MODEL_REASONING_EFFORTS[number];
export type PlatformModelPurpose = 'chat' | 'image';
/** The image model belongs to the Responses image_generation tool, not the top-level model field. */
export interface PlatformImageGeneration { model: string }
export interface PlatformModelCapabilities {
  family: 'gpt' | 'other';
  pdfInput: 'native-file' | 'text-images' | 'text-only' | 'unsupported';
  acceptsImages: boolean;
  nativePdfPageRendering: boolean;
  /** Raw native context window, before Core's own usable-window allowance. */
  contextWindow: number;
  autoCompactTokenLimit: number | null;
  /** Preserve means omit the native override and retain the existing provider behavior. */
  compaction: 'preserve' | 'local' | 'v2';
  reasoningEfforts: string[];
  defaultReasoningEffort: string | null;
}
export interface PlatformModelRates {
  /** Exact per-output image tariff in integer nano-CNY. Token tariffs remain independent. */
  imagePriceNanoCny?: string;
  inputPerMillion: number;
  outputPerMillion: number;
  cachedInputPerMillion: number;
  cacheWriteInputPerMillion: number;
  cacheWritePolicy: 'included_in_input' | 'separate';
}
export interface ModelConnectionTest {
  testedAt: string;
  revision: number;
  success: boolean;
  message: string;
  checks: Array<{ name: string; ok: boolean; message: string }>;
  latencyMs?: number;
}
/** Administrative DTO. Credentials are write-only and never enter this object. */
export interface PlatformModelRecord {
  id: string;
  displayName: string;
  supplier: string;
  upstreamModel: string;
  /** Missing in older configurations means chat only. */
  purposes?: PlatformModelPurpose[];
  imageGeneration?: PlatformImageGeneration;
  baseUrl: string;
  enabled: boolean;
  /** User-facing model selection. Missing in older records means true. */
  userSelectable?: boolean;
  isDefault: boolean;
  isImageDefault?: boolean;
  hasApiKey: boolean;
  credentialSource: 'environment' | 'stored';
  revision: number;
  capabilities: PlatformModelCapabilities;
  pricing: ModelPricing;
  test: ModelConnectionTest | null;
  createdAt: string;
  updatedAt: string;
}
export interface PlatformModelInput {
  /** Stable registry identity for trusted provisioning and existing records. */
  id: string;
  displayName: string;
  supplier: string;
  upstreamModel: string;
  /** Omit to preserve an existing configuration; new records default to chat only. */
  purposes?: PlatformModelPurpose[];
  imageGeneration?: PlatformImageGeneration;
  baseUrl: string;
  /** Omit when keeping the saved credential. Empty is not a request to reveal it. */
  apiKey?: string;
  enabled: boolean;
  /** Omit to preserve the existing value; new configurations default to true. */
  userSelectable?: boolean;
  capabilities: PlatformModelCapabilities;
  pricing: PlatformModelRates;
}
/** HTTP creation allocates a new internal identity on the server. */
export type PlatformModelCreateInput = Omit<PlatformModelInput, 'id'>;
export type PlatformModelUpdate = PlatformModelCreateInput & { revision: number };
export interface PlatformModelsPage { models: PlatformModelRecord[]; defaultModelId: string; defaultRevision: number; defaultImageModelId?: string | null; defaultImageRevision?: number }
export interface PlatformImageModelDefault { defaultImageModelId: string | null; defaultImageRevision: number }
export interface PlatformImageModelDefaultInput { modelId: string | null; revision?: number; defaultImageRevision: number }
export interface PlatformModelResponse { model: PlatformModelRecord; defaultRevision?: number }
export interface PlatformModelTestResponse extends PlatformModelResponse { result: ModelConnectionTest }
export interface PlatformModelCatalog { models: ModelInfo[]; defaultModelId: string; version?: string }
export interface AdminRoleUsersPage { users: User[]; total: number; page: number; pageSize: number }
export interface AdminRoleChange { role: 'user' | 'admin' }
export function canManagePlatform(role: User['role']): boolean { return role === 'admin' || role === 'super_admin'; }
export function canManageRoles(role: User['role']): boolean { return role === 'super_admin'; }
